import { useState, useEffect, useCallback, useRef } from 'react'
import { AlertCircle, Loader as SpinnerIcon } from 'lucide-react'
import { type CommandMilestoneResponse } from '../../lib/api'
import { useCommandMilestones, useCommandStatus } from '../../hooks/useCommandQuery'
import { useCommandSSE } from '../../hooks/useSSE'
import { polling } from '../../lib/polling'
import PostOperationChecklist from './PostOperationChecklist'

const INSTALL_MILESTONE_MESSAGES: Record<string, string> = {
  'verifying_prerequisites': 'Checking device settings...',
  'checking_supervision_status': 'Verifying device status...',
  'downloading_profile': 'Downloading enrollment profile...',
  'gathering_device_info': 'Gathering device information...',
  'creating_sparse_backup': 'Creating device backup...',
  'restoring_backup': 'Restoring backup (device will reboot)...',
  'verifying_supervision': 'Waiting for device to reconnect...',
  'registering_device': 'Registering device in system...',
  'confirming_enrollment': 'Waiting for device to enroll...',
}

const UNINSTALL_MILESTONE_MESSAGES: Record<string, string> = {
  'verifying_prerequisites': 'Checking device prerequisites...',
  'verifying_supervision_status': 'Verifying Unbreakable installation status...',
  'checking_backup_encryption': 'Checking backup encryption settings...',
  'creating_backup': 'Creating device backup...',
  'restoring_backup': 'Restoring backup to device (device will reboot)...',
  'verifying_removal': 'Verifying Unbreakable was uninstalled...',
  'confirming_removal': 'Confirming removal...',
}

const ANDROID_SETUP_MILESTONE_MESSAGES: Record<string, string> = {
  'checking_device': 'Checking device connection...',
  'checking_accounts': 'Checking for accounts on device...',
  'checking_device_owner': 'Verifying device status...',
  'downloading_agent': 'Downloading latest Unbreakable agent...',
  'installing_agent': 'Installing Unbreakable...',
  'setting_device_owner': 'Setting up device management...',
  'verifying_setup': 'Verifying setup...',
  'completing_setup': 'Completing setup...',
  'launching_agent': 'Starting agent...',
  'registering_device': 'Waiting for device to register...',
}

const ANDROID_REMOVE_MILESTONE_MESSAGES: Record<string, string> = {
  'check_connection': 'Checking device connection...',
  'remove_device_owner': 'Removing device management...',
  'verify_removal': 'Verifying removal...',
  'uninstall_app': 'Uninstalling Unbreakable...',
  'confirming_removal': 'Confirming removal...',
}

interface OperationProgressProps {
  commandId: string
  mode: 'install' | 'uninstall' | 'android_setup' | 'android_remove'
  onComplete: () => void
  onError: (errorMessage: string) => void
  /** Install only: fires when verifying_supervision completes (post-restore steps needed) */
  onPostRestoreReady?: () => void
  /** When set, uses school-scoped endpoints for polling */
  schoolId?: string
}

type ProgressState = 'active' | 'completed' | 'failed'

export default function OperationProgress({
  commandId,
  mode,
  onComplete,
  onError,
  onPostRestoreReady,
  schoolId,
}: OperationProgressProps) {
  const [state, setState] = useState<ProgressState>('active')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [shouldStopPolling, setShouldStopPolling] = useState(false)
  const [progressPercentage, setProgressPercentage] = useState(0)
  const [statusMessage, setStatusMessage] = useState('')
  const completedRef = useRef(false)

  const milestoneMessages = mode === 'android_setup'
    ? ANDROID_SETUP_MILESTONE_MESSAGES
    : mode === 'android_remove'
      ? ANDROID_REMOVE_MILESTONE_MESSAGES
      : mode === 'install'
        ? INSTALL_MILESTONE_MESSAGES
        : UNINSTALL_MILESTONE_MESSAGES
  const operationLabel = mode === 'android_setup' ? 'Setup' : mode === 'install' ? 'Installation' : 'Uninstallation'

  const markComplete = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    setState('completed')
    setProgressPercentage(100)
    setShouldStopPolling(true)
  }, [])

  const markFailed = useCallback((msg: string) => {
    if (completedRef.current) return
    completedRef.current = true
    setState('failed')
    setErrorMessage(msg)
    setShouldStopPolling(true)
    onError(msg)
  }, [onError])

  // SSE status callback
  const handleSSEStatus = useCallback((status: string) => {
    if (status === 'completed') markComplete()
    else if (status === 'failed') markFailed(`${operationLabel} failed`)
  }, [markComplete, markFailed, operationLabel])

  // SSE connection
  const { isConnected: sseConnected } = useCommandSSE(commandId, {
    enabled: !!commandId && !shouldStopPolling,
    onStatusUpdate: handleSSEStatus,
  })

  // Poll milestones
  const { data: milestonesData, error: milestonesError } = useCommandMilestones(
    shouldStopPolling ? '' : commandId,
    { refetchInterval: sseConnected ? polling.commandWithSSE : polling.commandFallback, schoolId }
  )

  // Poll command status
  const { data: command, error: commandError } = useCommandStatus(
    shouldStopPolling ? '' : commandId,
    { refetchInterval: sseConnected ? polling.commandWithSSE : polling.commandFallback, schoolId }
  )

  const pollingError = milestonesError || commandError

  // Process milestones
  useEffect(() => {
    if (completedRef.current || !milestonesData?.milestones.length) return

    const milestones = milestonesData.milestones

    // Update progress percentage (ratchet up only)
    let maxPercentage = 0
    for (const m of milestones) {
      if (m.percentage !== null && m.percentage > maxPercentage) {
        maxPercentage = m.percentage
      }
    }
    if (maxPercentage > 0) {
      setProgressPercentage(prev => Math.max(prev, maxPercentage))
    }

    // Check for failures
    const failedMilestone = milestones.find(m => m.status === 'failed')
    if (failedMilestone) {
      markFailed(failedMilestone.message || `${operationLabel} failed`)
      return
    }

    // Update status message from latest active milestone
    const activeMilestone = [...milestones].reverse().find(
      (m: CommandMilestoneResponse) => m.status === 'in_progress'
    )
    if (activeMilestone) {
      setStatusMessage(
        activeMilestone.message || milestoneMessages[activeMilestone.milestone] || 'Processing...'
      )
    }

    // Install: detect post-restore readiness
    if (mode === 'install' && onPostRestoreReady) {
      const verifyingDone = milestones.some(
        m => m.milestone === 'verifying_supervision' && m.status === 'completed'
      )
      if (verifyingDone) {
        onPostRestoreReady()
      }
    }

    // Uninstall: detect confirming_removal completion
    if (mode === 'uninstall') {
      const confirmingDone = milestones.some(
        m => m.milestone === 'confirming_removal' && m.status === 'completed'
      )
      if (confirmingDone) {
        markComplete()
      }
    }

    // Android setup: detect completing_setup completion
    if (mode === 'android_setup') {
      const setupDone = milestones.some(
        m => m.milestone === 'completing_setup' && m.status === 'completed'
      )
      if (setupDone) {
        markComplete()
      }
    }

    // Android remove: detect uninstall_app completion
    if (mode === 'android_remove') {
      const removeDone = milestones.some(
        m => m.milestone === 'uninstall_app' && m.status === 'completed'
      )
      if (removeDone) {
        markComplete()
      }
    }
  }, [milestonesData, markComplete, markFailed, milestoneMessages, mode, onPostRestoreReady, operationLabel])

  // Process command status
  useEffect(() => {
    if (completedRef.current || !command) return

    if (command.status === 'completed') {
      markComplete()
    } else if (command.status === 'failed') {
      markFailed(command.error || `${operationLabel} failed`)
    }
  }, [command, markComplete, markFailed, operationLabel])

  const isActive = state === 'active'

  // Completed state
  // Android: auto-advance to the modal's done step (which shows a richer completion card)
  // iOS: show post-operation checklist inline
  useEffect(() => {
    if (state === 'completed' && (mode === 'android_setup' || mode === 'android_remove')) {
      onComplete()
    }
  }, [state, mode, onComplete])

  if (state === 'completed') {
    if (mode === 'android_setup' || mode === 'android_remove') {
      return null
    }
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
            <div className="w-3 h-3 bg-green-500 rounded-full" />
          </div>
          <p className="text-lg font-medium text-green-600">
            {mode === 'install'
              ? 'Unbreakable installed successfully!'
              : 'Unbreakable uninstalled successfully!'}
          </p>
        </div>
        <PostOperationChecklist onDone={onComplete} />
      </div>
    )
  }

  // Failed state
  if (state === 'failed') {
    const isFindMyError = errorMessage?.toLowerCase().includes('find my') ||
      (command?.result as Record<string, unknown>)?.findMyEnabled === true

    return (
      <div className="space-y-5">
        <div className="text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-3">
            <AlertCircle size={28} className="text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{operationLabel} Failed</h3>
          <p className="text-sm text-gray-600">{errorMessage}</p>
        </div>

        {isFindMyError && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left">
            <p className="font-medium text-amber-800 mb-1">Find My iPhone is still enabled</p>
            <p className="text-sm text-amber-700">
              Go to <strong>Settings &rarr; [Your Name] &rarr; Find My &rarr; Find My iPhone</strong> and turn it <strong>OFF</strong>, then try again.
            </p>
          </div>
        )}

        {command?.error && (
          <details className="text-left">
            <summary className="text-sm text-red-600 cursor-pointer">Error Details</summary>
            <pre className="mt-2 text-xs text-red-800 bg-red-50 p-3 rounded overflow-auto">
              {command.error}
            </pre>
          </details>
        )}

        <button
          onClick={onComplete}
          className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
        >
          Back to Devices
        </button>
      </div>
    )
  }

  // Active state — show progress
  return (
    <div className="space-y-6">
      <div className="text-center">
        <SpinnerIcon size={56} className="animate-spin mx-auto text-brand mb-4" />
        <p className="text-lg text-gray-900 font-medium">
          {statusMessage || `Preparing ${mode === 'install' ? 'installation' : 'removal'}...`}
        </p>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm font-semibold text-brand">{progressPercentage}%</span>
        </div>
        <div
          className="w-full bg-gray-200 rounded-full h-3 overflow-hidden"
          role="progressbar"
          aria-valuenow={progressPercentage}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="bg-linear-to-r from-brand to-[#6b5a88] h-3 rounded-full transition-all duration-500 ease-out relative"
            style={{ width: `${Math.max(progressPercentage, 2)}%` }}
          >
            {isActive && (
              <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
            )}
          </div>
        </div>
      </div>

      <p className="text-sm text-gray-500 text-center">
        {mode === 'android_setup'
          ? 'Please keep the phone connected via USB until setup is complete.'
          : 'Your device will reboot during this process. Please do not disconnect it.'}
      </p>

      {/* Transient polling errors */}
      {pollingError && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
          <p className="text-sm text-yellow-800">
            Having trouble checking status. The process may still be running.
          </p>
        </div>
      )}
    </div>
  )
}
